import KendoTable, {
  type ColumnConfig,
} from "@/app/components/common/table/KendoTable";
import {
  Checkbox,
  type CheckboxChangeEvent,
} from "@progress/kendo-react-inputs";
import {
  DateCellOne,
  NoOfApproverCell,
  type CustomAmountCellProps,
} from "@/app/components/common/table/KendoCustomCells";
import type { PaymentsBulkApprovalViewSummaryResponse } from "@/app/types/bulkPaymentsPayroll";
import "@/app/page/default/Dashboard/Dashboard.scss";
import type { BulkUploadApproveTableProps } from "../BulkUpload";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { t } from "i18next";

export const CustomAmountCell: React.FC<CustomAmountCellProps> = ({
  dataItem,
  showDecimals = true,
}) => {
  const amount =
    typeof dataItem?.Amount === "string"
      ? Number(dataItem.Amount)
      : dataItem?.Amount;

  const { integer, decimal, symbol } = gxCurrencyFormat(
    Number.isNaN(amount) ? undefined : amount,
    dataItem?.baseCurrency || "",
  );

  return (
    <div className="amount-cell">
      <span className="amount-currency gx-p12-b">{symbol}</span>
      <span className="amount-whole gx-p12-b">{integer}</span>
      {showDecimals && (
        <span className="amount-decimal gx-p12-m">{decimal || ".00"}</span>
      )}
    </div>
  );
};

function growXRefCell(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  return <td>{props.dataItem?.gxJobChnNo || "--"}</td>;
}

function totalTxnCell(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  return <td className="text-center">{props.dataItem?.totalTxn || 0}</td>;
}

function totalTxnCountCell(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  return <td className="text-center">{props.dataItem?.totalTxnCount || 0}</td>;
}

function fileAmountCellShowAll(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  const amount = props.dataItem?.totalAmount ?? 0;
  const baseCurrency = props.dataItem?.baseCurrency;

  return (
    <td className="text-center">
      <CustomAmountCell
        dataItem={{ Amount: amount, baseCurrency }}
        showDecimals={true}
      />
    </td>
  );
}

function fileAmountCell(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  const amount = props.dataItem?.totalAmount || 0;
  return (
    <td>
      <span className="gx-p12-b">
        {amount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
    </td>
  );
}

function validTxnCell(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  return <td className="text-center">{props.dataItem?.validTxn || 0}</td>;
}

function validTxnCountCell(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  return <td className="text-center">{props.dataItem?.validTxnCount || 0}</td>;
}

function invalidTxnCell(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  return <td className="text-center">{props.dataItem?.invalidTxn || 0}</td>;
}

function invalidTxnCountCell(props: {
  dataItem?: PaymentsBulkApprovalViewSummaryResponse;
}) {
  return (
    <td className="text-center">{props.dataItem?.invalidTxnCount || 0}</td>
  );
}

const BulkUploadApproveTable: React.FC<BulkUploadApproveTableProps> = ({
  onRowClick,
  showAll = false,
  bulkUploads,
  selectedBulkUploads = [],
  setSelectedBulkUploads,
  setCheckAll,
  checkAll = false,
  approveAccess = false,
  isLoading,
  maxSelectionLimit = 5,
  hideCbCheckbox = false,
}) => {
  function checkboxHeaderCell() {
    if (!approveAccess) return null;
    return (
      <Checkbox
        className="gx-font-medium"
        checked={checkAll}
        onChange={(event: CheckboxChangeEvent) => {
          if (!setCheckAll || !setSelectedBulkUploads) return;
          if (event?.value) {
            const toSelect = bulkUploads
              .slice(0, maxSelectionLimit)
              .map((item) => item.gxJobProcessid);
            setSelectedBulkUploads(toSelect);
          } else {
            setSelectedBulkUploads([]);
            setCheckAll(false);
          }
        }}
        disabled={!approveAccess}
      />
    );
  }

  function checkboxRowCell(props: {
    dataItem?: PaymentsBulkApprovalViewSummaryResponse;
  }) {
    const id = props.dataItem?.gxJobProcessid;
    if (id === undefined) return <td />;

    const isSelected = selectedBulkUploads.includes(id);
    const isDisabled =
      !isSelected && selectedBulkUploads.length >= maxSelectionLimit;

    return (
      <td className="check-box-cell" onClick={(e) => e.stopPropagation()}>
        {approveAccess && (
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              if (!setSelectedBulkUploads) return;
              if (isSelected) {
                setSelectedBulkUploads(
                  selectedBulkUploads.filter((x) => x !== id),
                );
              } else if (selectedBulkUploads.length < maxSelectionLimit) {
                setSelectedBulkUploads((prev) => [...prev, id]);
              }
            }}
          />
        )}
      </td>
    );
  }

  const columns: ColumnConfig[] =
    showAll === false
      ? [
          {
            field: "gxChnNo",
            title: t("growx_ref_no"),
            width: 150,
            cell: growXRefCell,
          },
          {
            field: "crtdDt",
            title: t("uploaded_on"),
            width: 150,
            cell: DateCellOne,
          },
          {
            field: "totalAmount",
            title: t("total_txn"),
            width: 120,
            cell: totalTxnCell,
          },
          {
            field: "fileAmount",
            title: t("file_amount"),
            width: 150,
            cell: fileAmountCell,
          },
          {
            field: "validTxn",
            title: t("valid_txn"),
            width: 120,
            cell: validTxnCell,
          },
          {
            field: "invalidTxn",
            title: t("invalid_txn"),
            width: 120,
            cell: invalidTxnCell,
          },
          {
            field: "approvalLevel",
            title: t("approval_level"),
            width: 120,
            cell: NoOfApproverCell,
          },
        ]
      : [
          ...(!hideCbCheckbox
            ? [
                {
                  field: "",
                  headerCell: checkboxHeaderCell(),
                  cell: checkboxRowCell,
                  width: 45,
                },
              ]
            : []), // Checkbox column - skip entirely when CB
          {
            field: "gxChnNo",
            title: t("growx_ref_no"),
            width: 150,
            cell: growXRefCell,
          },
          {
            field: "crtdDt",
            title: t("uploaded_on"),
            width: 150,
            cell: DateCellOne,
          },
          {
            field: "totalTxnCount",
            title: t("total_txn"),
            width: 120,
            cell: totalTxnCountCell,
          },
          {
            field: "totalAmount",
            title: t("file_amount"),
            width: 150,
            cell: fileAmountCellShowAll,
          },
          {
            field: "validTxnCount",
            title: t("valid_txn"),
            width: 120,
            cell: validTxnCountCell,
          },
          {
            field: "invalidTxnCount",
            title: t("invalid_txn"),
            width: 120,
            cell: invalidTxnCountCell,
          },
          {
            field: "approvalLevel",
            title: t("approval_level"),
            width: 120,
            cell: NoOfApproverCell,
          },
        ];

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <output className="spinner-border text-primary">
          <span className="gx-visually-hidden gx-font-regular">{t("loading__")}</span>
        </output>

        <p className="mt-2">{t("loading_payment_data")}</p>
      </div>
    );
  }

  return (
    <>
      {selectedBulkUploads.length > 0 && (
        <div className="mb-3 p-2 bg-light rounded">
          <span className="gx-p12-b">
            {selectedBulkUploads.length} {t("of_text")} {maxSelectionLimit}{" "}
            {t("records_selected")}
          </span>
        </div>
      )}

      <KendoTable
        data={bulkUploads}
        columns={columns}
        onRowClick={onRowClick}
        className="kendo-grid-bulk-uploads"
      />
    </>
  );
};

export default BulkUploadApproveTable;
